import React, {FC, PropsWithChildren, useEffect, useReducer} from 'react';
import {useGeocodingCore} from '@mapbox/search-js-react';
import {MAPBOX_TOKEN} from 'components/BaseMap/constants';
import {createBrowserHistory} from 'history';
import {cookies, CookKey} from 'utils/cookies';
import {useUserCoords} from 'utils/geolocate';
import {useQueryValues} from 'utils/queryObj';
import {AllRegions} from 'utils/region';

import {actions} from './actions';
import {calcRegion} from './calcRegion';
import {reducer} from './reducer';
import {Location} from './types';

const emptyLocation = {} as Location;
const LocationContext = React.createContext<Location>(emptyLocation);

interface ProviderProps {
	location: Location;
}

export const LocationProvider: FC<PropsWithChildren<ProviderProps>> = ({
	children,
	location,
}) => (
	<LocationContext.Provider value={location}>
		{children}
	</LocationContext.Provider>
);

export const useContextLocation = () => React.useContext(LocationContext);

export const useCreateLocation = (): Location => {
	const geocode = useGeocodingCore({accessToken: MAPBOX_TOKEN});
	const {read} = useQueryValues();
	const [configs, dispatch] = useReducer(reducer, {});

	// Do initial processing and loading of region
	useEffect(() => {
		const {region: qkey} = read({region: ''});
		const ckey = cookies.get(CookKey.Region);

		// Try to find a valid region from query or cookie
		const region = AllRegions.find((r) => {
			if (qkey) {
				return r.key === qkey;
			} else if (ckey) {
				return r.name === ckey;
			}
		});

		// Set if anything found
		if (region) {
			dispatch({type: 'SET-REGION', region});
		}

		// Clear from query if anything is set
		if (qkey) {
			createBrowserHistory().replace({
				search: '',
			});
		}
	}, []);

	// Update cookie saving region on every update
	useEffect(() => {
		const name = configs.region?.name;
		if (name) {
			cookies.set(CookKey.Region, name, {expires: 90});
		} else {
			cookies.remove(CookKey.Region);
		}
	}, [configs.region?.name]);

	// Attempt to read browser lat/lng on page load
	useUserCoords(async (coords, source) => {
		if (!coords.lat && !coords.lng) {
			return;
		} else {
			dispatch({type: 'SET-USER-COORDS', coords});
		}

		// Determine if the coordinates are inside a service
		// region. Skip all auto-config if it is not.
		const region = calcRegion(coords);
		if (!region) {
			return;
		}

		// If source is IP, use for region search only
		if (source === 'IP') {
			dispatch({type: 'SET-REGION', region, skipIf: 'set'});
			return;
		}

		// GPS coords, reverse geocode to determine label (address)
		const result = await geocode.reverse(coords, {
			country: 'US',
			limit: 1,
			types: 'address',
		});

		const feature = result.features.find(
			(f) => f.properties.feature_type === 'address',
		);

		// No address, fall back to setting the nearest region.
		if (!feature) {
			dispatch({type: 'SET-REGION', region, skipIf: 'set'});
			return;
		}

		// Found! Set label and coordinates for best selection.
		const label = feature.properties.name_preferred;
		dispatch({type: 'SET-USER-COORDS', coords, label});
		dispatch({type: 'SET-COORDS', coords, label, skipIf: 'mismatched'});
	});

	return {
		configs,
		actions: actions(dispatch),
	};
};
